import React, { useCallback, useMemo } from "react";
import { z } from "zod";
import type { ToolComponentProps } from "@zapier/ai-toolkit";
import { Button } from "@zapier/design-system";
import { useCopilotChat } from "@zapier/ai-toolkit/client";
import { atom } from "jotai";

import styles from "./buildModeSuggestionsClient.module.css";

/**
 * Tool parameters for showing build mode suggestion pills
 * Currently focused on field mapping functionality
 */
export const BuildModeSuggestionsParamsSchema = z.object({
  suggestions: z.array(z.string()).optional().default(["🚀 Setup My Zaps"]),
});

export type BuildModeSuggestionsParams = z.infer<
  typeof BuildModeSuggestionsParamsSchema
>;

export type BuildModeSuggestionsResult = {
  success: boolean;
  message: string;
};

/**
 * Component for rendering build mode suggestion pills
 * Currently focused on field mapping functionality
 */
const BuildModeSuggestions = (props: ToolComponentProps<any, any>) => {
  const { args } = props;
  const { suggestions } = args as BuildModeSuggestionsParams;

  const { setMessages, reload } = useCopilotChat();

  // Map suggestions to their corresponding prompts - memoized to prevent re-renders
  const promptMapping = useMemo(
    () =>
      ({
        "🚀 Setup My Zaps":
          "Help me configure field mapping for the Zaps on this canvas. Please work through them one at a time and ask for any additional information you need.",
      }) as Record<string, string>,
    [],
  );

  const handleSuggestionClick = useCallback(
    async (suggestion: string) => {
      const promptText = promptMapping[suggestion] || suggestion;

      // Use setMessages to append user message to conversation
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          role: "user",
          content: promptText,
          id: `user-${Date.now()}`,
          createdAt: new Date(),
          parts: [{ type: "text", text: promptText }],
        },
      ]);

      // Trigger LLM response after adding the user message
      await reload();
    },
    [promptMapping, setMessages, reload],
  );

  return (
    <div className={styles.container}>
      {suggestions.map((suggestion) => (
        <Button
          key={suggestion}
          variant="primary"
          size="compact"
          onPress={() => handleSuggestionClick(suggestion)}
          className={styles.suggestionButton}
        >
          {suggestion}
        </Button>
      ))}
    </div>
  );
};

/**
 * Build mode suggestions client tool for Canvas AI toolkit
 * Shows interactive suggestion pills that users can click to continue the conversation
 */
export const show_build_mode_suggestions = {
  name: "show_build_mode_suggestions",
  description:
    "Show interactive suggestion pills for common build mode actions",
  parameters: BuildModeSuggestionsParamsSchema,
  component: BuildModeSuggestions,
  execute: atom(
    null,
    async (
      get,
      set,
      args: BuildModeSuggestionsParams,
    ): Promise<BuildModeSuggestionsResult> => {
      const { suggestions } = args;
      return {
        success: true,
        message: `Showing ${suggestions.length} build mode suggestion pill(s)`,
      };
    },
  ),
};
